import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { TOAST } from '../utils/toastMessages';
import { MessageSquare } from 'lucide-react';
import { formatLongDate } from '../utils/dateFormatter';
import ComposerSheet from './ui/ComposerSheet';

const FeedbackDialog = ({ isOpen, onClose, trigger, userRole, inquiries = [], preSelectedInquiry = null }) => {
  const getInitialCategory = () => {
    if (preSelectedInquiry) {
      return userRole === 'customer' ? 'report_vendor' : 'report_customer';
    }
    return 'bug';
  };

  const [formData, setFormData] = useState({
    category: getInitialCategory(),
    subject: '',
    description: '',
    inquiry_id: preSelectedInquiry?.id || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (preSelectedInquiry) {
      const reportingEntity = userRole === 'customer' 
        ? preSelectedInquiry.vendor_name || 'vendor'
        : preSelectedInquiry.customer_name || 'customer';
      
      setFormData({
        category: userRole === 'customer' ? 'report_vendor' : 'report_customer',
        subject: `Issue with ${reportingEntity} - ${preSelectedInquiry.event_type}`,
        description: '',
        inquiry_id: preSelectedInquiry.id
      });
    }
  }, [preSelectedInquiry, userRole]);

  const handleSubmit = async () => {
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    if ((formData.category === 'report_vendor' || formData.category === 'report_customer') && !formData.inquiry_id) {
      toast.error('Please select a request to report');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const page_url = window.location.href;
      const device_info = `${navigator.userAgent} | Screen: ${window.screen.width}x${window.screen.height} | Viewport: ${window.innerWidth}x${window.innerHeight}`;
      
      await axios.post(`${API}/feedback`, { ...formData, page_url, device_info }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(TOAST.FEEDBACK_SUBMITTED);
      setFormData({ category: 'bug', subject: '', description: '', inquiry_id: '' });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = (
    <div className="space-y-4">
      <div>
        <Label htmlFor="category">Category *</Label>
        <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value, inquiry_id: '' })}>
          <SelectTrigger id="category" data-testid="feedback-category"><SelectValue placeholder="Select category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="bug">Bug / Something broken</SelectItem>
            <SelectItem value="feature_request">Feature Request</SelectItem>
            <SelectItem value="confusing">Something confusing</SelectItem>
            {userRole === 'customer' && <SelectItem value="report_vendor">Report Vendor</SelectItem>}
            {userRole === 'vendor' && <SelectItem value="report_customer">Report Customer</SelectItem>}
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(formData.category === 'report_vendor' || formData.category === 'report_customer') && (
        <div>
          <Label htmlFor="inquiry">Select Request *</Label>
          <Select value={formData.inquiry_id} onValueChange={(value) => setFormData({ ...formData, inquiry_id: value })}>
            <SelectTrigger id="inquiry" data-testid="feedback-inquiry"><SelectValue placeholder="Choose request to report..." /></SelectTrigger>
            <SelectContent>
              {inquiries.length === 0 ? (
                <SelectItem value="none" disabled>No requests available</SelectItem>
              ) : inquiries.map((inquiry) => (
                <SelectItem key={inquiry.id} value={inquiry.id}>
                  {formData.category === 'report_vendor'
                    ? `${inquiry.vendor_name || 'Vendor'} - ${inquiry.event_type} (${formatLongDate(inquiry.event_date)})`
                    : `${inquiry.customer_name || 'Customer'} - ${inquiry.event_type} (${formatLongDate(inquiry.event_date)})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">
            {formData.category === 'report_vendor' ? 'Select the request with the vendor you want to report' : 'Select the request with the customer you want to report'}
          </p>
        </div>
      )}

      <div>
        <Label htmlFor="subject">Subject *</Label>
        <Input id="subject" placeholder="Brief summary of your feedback" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} maxLength={200} data-testid="feedback-subject" />
        <p className="text-xs text-gray-500 mt-1">{formData.subject.length}/200</p>
      </div>

      <div>
        <Label htmlFor="description">Description *</Label>
        <Textarea id="description" placeholder="Please provide as much detail as possible..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={5} data-testid="feedback-description" />
      </div>

      <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-800">
        <p className="font-medium mb-1">Auto-captured information:</p>
        <ul className="text-xs space-y-0.5">
          <li>Page: {typeof window !== 'undefined' ? window.location.pathname : ''}</li>
          <li>Screen: {typeof window !== 'undefined' ? `${window.screen.width}x${window.screen.height}` : ''}</li>
        </ul>
      </div>
    </div>
  );

  return (
    <>
      {trigger}
      <ComposerSheet
        open={isOpen}
        onClose={onClose}
        title="Send Feedback"
        subtitle="Help us improve by reporting bugs, suggesting features, or sharing your thoughts"
        icon={<MessageSquare className="h-5 w-5 text-blue-600" />}
        testId="feedback-composer"
        maxWidth="sm:max-w-2xl"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting} className="flex-1">Cancel</Button>
            <Button onClick={handleSubmit} disabled={isSubmitting || !formData.subject.trim() || !formData.description.trim()} className="flex-1" data-testid="submit-feedback-btn">
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        }
      >
        {content}
      </ComposerSheet>
    </>
  );
};

export default FeedbackDialog;
